import type Post from '@/models/post';
import type { ListSlot } from '@/models/listTypes';
import type { SocialPlatform } from '@/utils/platformDetection';
import type { PlaceCardData } from '@/types/listViewTypes';
import {
  buildCityDataFromPlaceData,
  extractCityCandidates,
} from '@/utils/cityExtractionUtils';
import { buildListPostPayload } from '@/utils/listBuilder';
import { createOrUpdateListPost } from '@/api/list';
import {
  addPlacesToItineraryAtomic,
  addPlacesToListAtomic,
  createPost,
} from '@/api/itinerary';
import type { ImportAppendToExistingOptions } from '@/utils/itineraryImportAppend';
import {
  type SourceMetadata,
  buildItineraryPostObject,
} from '@/utils/postBuilder';
import { enrichPlaceCardDataForListSave } from '@/utils/enrichPlaceCardForListSave';
import { runWithConcurrencyLimit } from '@/utils/concurrencyUtils';
import { saveLocation } from '@/api/locationSaves';
import { isMongoObjectIdString, mongoObjectIdStringOrNull } from '@/utils/idGeneration';
import type { SocialScraperResponse } from '@/services/socialScraperService';
import {
  transformSocialLocationToItinerarySlot,
} from '@/utils/socialScraperTransform';
import { getPhotoUrls } from '@/utils/photoUtils';
import type { CityFormData, PrimaryDestination } from '@/types/city';
import type { CreatePostPayload, ItinerarySlot } from '@/utils/itinerary/types';

export interface SocialImportContext {
  socialUrl: string;
  platform: SocialPlatform;
  response: SocialScraperResponse;
  itinerarySlots: ItinerarySlot[];
  listSlots: ListSlot[];
  city: CityFormData | null;
  primaryDestination: PrimaryDestination | null;
  tripTitle: string;
  listTitle: string;
  sourceMetadata: SourceMetadata;
  sourceSocialMediaPostId: string | null;
}

export interface SocialImportPlace {
  id: string;
  title: string;
  city: string;
  country: string;
  address: string;
  imageUrl: string | null;
  placeCardData: PlaceCardData;
}

export interface SocialImportDestination {
  type: 'itinerary' | 'list';
  id: string;
  name: string;
  isNew: boolean;
  seedPlaceId?: string;
}

/**
 * Destination shape when the Import Inspo bulk-save primary path lands places
 * directly into the user's Saves (no list/itinerary), auto-bucketed by the
 * dominant country. `name` is the country name shown on the success card.
 * `name` is null when no place had a resolvable country — the success screen
 * falls back to a generic headline in that case. `stateName` is populated when
 * the dominant country is "United States" and a US state was parseable from
 * the selected places' addresses — the success card then deep-links into the
 * state page instead of the state grid.
 */
export interface SocialImportSavesDestination {
  type: 'saves';
  name: string | null;
  stateName?: string | null;
}

export type SocialImportSummaryDestination = SocialImportDestination | SocialImportSavesDestination;

export interface SocialImportBulkSaveResult {
  savedCount: number;
  failedCount: number;
  failedPlaceTitles: string[];
}

function buildFallbackCity(response: SocialScraperResponse): CityFormData | null {
  const firstLocationWithPlace = response.locations.find((row) => row.place !== null);
  if (!firstLocationWithPlace?.place) {
    return null;
  }

  const place = firstLocationWithPlace.place;
  const city = place.address.city?.trim();
  const country = place.address.country?.trim();
  if (!city) {
    return null;
  }

  const cityIdentifyingName = country ? `${city}, ${country}` : city;
  return {
    city,
    name: city,
    city_identifying_name: cityIdentifyingName,
    a3_intl_address: cityIdentifyingName,
    google_places_data: {
      location: {
        latitude: place.coordinates.lat,
        longitude: place.coordinates.lng,
      },
    },
    geoposition: {
      type: 'Point',
      coordinates: [place.coordinates.lng, place.coordinates.lat],
    },
  };
}

function inferPrimaryDestination(city: CityFormData | null): PrimaryDestination | null {
  if (!city?.city_identifying_name) {
    return null;
  }

  return {
    type: 'city',
    identifying_name: city.city_identifying_name,
    ...(city.google_place_id ? { google_place_id: city.google_place_id } : {}),
    ...(city.geoposition ? { geoposition: city.geoposition } : {}),
  };
}

function buildTitles(city: CityFormData | null): { tripTitle: string; listTitle: string } {
  const cityName = city?.city?.trim();
  if (!cityName) {
    return {
      tripTitle: 'Imported trip',
      listTitle: 'Imported list',
    };
  }

  return {
    tripTitle: `${cityName} trip`,
    listTitle: `${cityName} spots`,
  };
}

function mapResponseLocationToSocialImportPlace(
  location: SocialScraperResponse['locations'][0],
  index: number,
): SocialImportPlace | null {
  if (!location.place) {
    return null;
  }

  const { place } = location;
  const imageUrls = getPhotoUrls(place.photos, 400);
  const placeId = `${place.placeId}-${index}`;
  const address = place.address?.full || place.address?.short || '';
  const city = place.address?.city || location.city || '';
  const country = place.address?.country || location.country || '';
  const primaryDisplay = place.category?.primaryDisplay || 'Place';

  return {
    id: placeId,
    title: place.name,
    city,
    country,
    address,
    imageUrl: imageUrls[0] ?? null,
    placeCardData: {
      id: placeId,
      title: place.name,
      address,
      cityName: city,
      imageUrls,
      googlePlaceId: place.placeId,
      placeId: place.placeId,
      cuisine: primaryDisplay,
      locationType: `${city || 'Unknown'} | ${primaryDisplay}`,
      ...(place.contact?.website ? { website: place.contact.website } : {}),
      latitude: place.coordinates.lat,
      longitude: place.coordinates.lng,
    },
  };
}

function mapItineraryToListSlots(itinerarySlots: ItinerarySlot[]): ListSlot[] {
  return itinerarySlots.map((slot, index) => {
    const locationData = slot.locationData as
      | { _id?: string; google_place_id?: string; geoposition?: { type?: string; coordinates?: [number, number] } }
      | undefined;
    const name = (slot.locationData?.name as string | undefined) ?? slot.name ?? '';

    return {
      sequenceIndex: index,
      locationData: {
        ...(locationData?._id ? { _id: locationData._id } : {}),
        name,
        ...(locationData?.google_place_id ? { google_place_id: locationData.google_place_id } : {}),
        ...(locationData?.geoposition?.type === 'Point' && Array.isArray(locationData.geoposition.coordinates)
          ? { geoposition: { type: 'Point' as const, coordinates: locationData.geoposition.coordinates } }
          : {}),
      },
      name,
      placeId: slot.placeId,
      slotType: slot.slotType,
      notes: slot.notes,
      budget: slot.budget,
      cost: slot.cost,
      timeRange: slot.timeRange,
      image_urls: Array.isArray(slot.image_urls) ? (slot.image_urls as string[]) : undefined,
      address: typeof slot.address === 'string' ? slot.address : undefined,
      isUserCreated: !!slot.isUserCreated,
    };
  });
}

export function buildSocialImportPlaces(response: SocialScraperResponse): SocialImportPlace[] {
  return response.locations
    .map((location, index) => mapResponseLocationToSocialImportPlace(location, index))
    .filter((place): place is SocialImportPlace => place !== null);
}

export function buildSocialImportContext(
  socialUrl: string,
  platform: SocialPlatform,
  response: SocialScraperResponse,
): SocialImportContext {
  const itinerarySlots = response.locations
    .map((location, index) => transformSocialLocationToItinerarySlot(location, 0, index))
    .filter((slot): slot is ItinerarySlot => slot !== null);

  const cityCandidates = extractCityCandidates(response.locations);
  const city = cityCandidates.length > 0
    ? (buildCityDataFromPlaceData(cityCandidates[0], response.locations) as CityFormData)
    : buildFallbackCity(response);

  const { tripTitle, listTitle } = buildTitles(city);
  const sourceMetadata: SourceMetadata = {
    source: 'inspo-to-trip',
    postId: response.postId,
    shortCode: response.shortCode,
    platform,
    shared_url: socialUrl,
  };

  return {
    socialUrl,
    platform,
    response,
    itinerarySlots,
    listSlots: mapItineraryToListSlots(itinerarySlots),
    city,
    primaryDestination: inferPrimaryDestination(city),
    tripTitle,
    listTitle,
    sourceMetadata,
    sourceSocialMediaPostId: mongoObjectIdStringOrNull(response.postId),
  };
}

export async function addSocialImportPlacesToDestination(
  destination: SocialImportDestination,
  selectedPlaces: SocialImportPlace[],
  appendOptions?: ImportAppendToExistingOptions,
): Promise<SocialImportBulkSaveResult> {
  let savedCount = 0;
  const placesToAdd: PlaceCardData[] = [];

  for (const place of selectedPlaces) {
    const isSeededPlace = destination.isNew
      && Boolean(destination.seedPlaceId)
      && destination.seedPlaceId === place.id;
    if (isSeededPlace) {
      savedCount += 1;
      continue;
    }
    placesToAdd.push(place.placeCardData);
  }

  if (placesToAdd.length === 0) {
    return {
      savedCount,
      failedCount: 0,
      failedPlaceTitles: [],
    };
  }

  // Ensure both list and itinerary add-to-existing flows have persisted Mongo location ids
  // for Google-backed places when available.
  const enrichedPlacesToAdd = await Promise.all(
    placesToAdd.map(async (place) => {
      try {
        return await enrichPlaceCardDataForListSave(place);
      } catch {
        // Preserve prior behavior: a single enrichment failure should not abort the import.
        return place;
      }
    }),
  );

  if (destination.type === 'itinerary') {
    await addPlacesToItineraryAtomic(destination.id, enrichedPlacesToAdd, appendOptions);
  } else {
    await addPlacesToListAtomic(destination.id, enrichedPlacesToAdd, appendOptions);
  }

  savedCount += placesToAdd.length;

  return {
    savedCount,
    failedCount: 0,
    failedPlaceTitles: [],
  };
}

const BULK_SAVE_CONCURRENCY = 5;

/**
 * Bulk-save selected social-import places into the user's Saves (autobucket by
 * destination country on read). Each save is an individual `saveLocation` call
 * carrying `sourcePostId` from the parse context so the origin post is
 * attributed. Bounded parallelism keeps large reels responsive without
 * hammering the backend. Per-place failures are aggregated into the returned
 * SocialImportBulkSaveResult; the whole batch does not abort on one failure.
 */
export async function bulkSaveSocialImportPlaces(
  selectedPlaces: SocialImportPlace[],
  context: Pick<SocialImportContext, 'sourceSocialMediaPostId'>,
): Promise<SocialImportBulkSaveResult> {
  if (selectedPlaces.length === 0) {
    return { savedCount: 0, failedCount: 0, failedPlaceTitles: [] };
  }

  const sourcePostId = mongoObjectIdStringOrNull(context.sourceSocialMediaPostId) ?? undefined;

  const outcomes = await runWithConcurrencyLimit(
    selectedPlaces.map((place) => async (): Promise<{ ok: boolean; title: string }> => {
      const title = place.title || 'Untitled place';
      try {
        const enriched = await enrichPlaceCardDataForListSave(place.placeCardData);
        const locationId = enriched.locationId ?? '';
        if (!isMongoObjectIdString(locationId)) {
          return { ok: false, title };
        }
        await saveLocation({
          locationId,
          ...(sourcePostId ? { sourcePostId } : {}),
        });
        return { ok: true, title };
      } catch {
        return { ok: false, title };
      }
    }),
    BULK_SAVE_CONCURRENCY,
  );

  const failedPlaceTitles = outcomes.filter((o) => !o.ok).map((o) => o.title);
  const savedCount = outcomes.length - failedPlaceTitles.length;

  return {
    savedCount,
    failedCount: failedPlaceTitles.length,
    failedPlaceTitles,
  };
}

export async function createItineraryFromSocialContext(
  context: SocialImportContext,
): Promise<Post> {
  const itineraryPayload = await buildItineraryPostObject({
    isEdit: false,
    editingPostId: null,
    urlPostId: null,
    remixPostObj: null,
    formData: {
      selectedTripTypes: [],
      selectedTravelWithTypes: [],
      selectedBudget: null,
      collaborators: [],
      is_private: true,
      primary_destination: context.primaryDestination,
    },
    tripTitle: context.tripTitle,
    actualDayCount: 1,
    city: context.city,
    itineraryData: context.itinerarySlots,
    sourceMetadata: context.sourceMetadata,
    sourceSocialMediaPostId: context.sourceSocialMediaPostId,
    isDraft: false,
    isPrivate: true,
  });

  return createPost(itineraryPayload as unknown as CreatePostPayload);
}

export async function createListFromSocialContext(
  context: SocialImportContext,
): Promise<Post> {
  const listPayload = buildListPostPayload({
    isEdit: false,
    editingPostId: null,
    remixPostObj: null,
    formData: {
      selectedTripTypes: [],
      selectedTravelWithTypes: [],
      selectedBudget: null,
      is_private: true,
      primary_destination: context.primaryDestination,
    },
    tripTitle: context.listTitle,
    city: context.city,
    slots: context.listSlots,
    isDraft: false,
    isPrivate: true,
  });

  return createOrUpdateListPost(listPayload as unknown as Record<string, unknown>);
}

const CAPTION_URL_REGEX = /https?:\/\/\S+/g;

export const SUGGESTED_TITLE_MAX_LENGTH = 50;

export function suggestListTitle(
  response: SocialScraperResponse,
  fallbackTitle: string,
): string {
  const rawCaption = response.post.caption?.trim() ?? '';
  const firstLine = rawCaption.split('\n')[0] ?? '';
  const cleaned = firstLine
    .replace(CAPTION_URL_REGEX, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) {
    return fallbackTitle;
  }
  if (cleaned.length > SUGGESTED_TITLE_MAX_LENGTH) {
    return cleaned.slice(0, SUGGESTED_TITLE_MAX_LENGTH).trimEnd();
  }
  return cleaned;
}
